import { useTranslation } from 'react-i18next';
import { useDocumentParams } from 'app/router/params';
import { useState, ChangeEvent } from 'react';
import { StructurePreview, Template } from 'app/components/StructurePreview';
import { useSelectedTemplates, useUpdateSelectedTemplates, useTemplates } from 'entities/template';
import { Spinner, Modal } from 'app/components';
import { useUser } from 'app/state/contexts';
import { usePermissions } from 'app/api/permission-queries';
import { useDesignDraft } from 'app/api/documents/useDesignDraft';

type PageStructure = Template & { superId: string; name: string };

type GraphicalVariables = Record<string, string | undefined>;

const useDocumentAspectRatio = () => {
  const { style, draftEntry } = useDesignDraft({ styleType: 'graphical-variables', parsed: true });
  const documentVariables = (style.data?.content ?? {}) as GraphicalVariables;
  const draftVariables = (draftEntry?.style ?? {}) as GraphicalVariables;
  const variables = { ...documentVariables, ...draftVariables };
  const w = parseInt(variables.pageWidth ?? '', 10) || 794;
  const h = parseInt(variables.pageHeight ?? '', 10) || 1123;
  return `${w} / ${h}`;
};

interface StructureModalProps {
  show: boolean;
  selected: string[];
  onClose: () => void;
  pageStructures: PageStructure[];
  aspectRatio: string;
}

const StructureModal = ({ show, selected, onClose, pageStructures, aspectRatio }: StructureModalProps) => {
  const { t } = useTranslation(['common', 'theme']);

  const { documentId } = useDocumentParams();

  const [newSelected, setNewSelected] = useState<string[]>(selected);
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const { mutate: saveSelectedTemplates } = useUpdateSelectedTemplates(documentId);

  const handlePreviewClick = (superId: string) => {
    if (newSelected.includes(superId)) {
      setNewSelected(newSelected.filter((s) => s !== superId));
    } else {
      setNewSelected([...newSelected, superId]);
    }
  };

  const handleSave = () => {
    saveSelectedTemplates(newSelected, { onSuccess: onClose });
  };

  const filtered = pageStructures.filter((page) => {
    const matchesSearch = !search || page.name.toLowerCase().includes(search.toLowerCase());
    const matchesScope =
      scopeFilter === 'all' ||
      (scopeFilter === 'global' && !page.organization && !page.documentSuperId) ||
      (scopeFilter === 'organization' && page.organization && !page.documentSuperId) ||
      (scopeFilter === 'selected' && selected.includes(page.superId));
    return matchesSearch && matchesScope;
  });

  return (
    <Modal
      titleLabelKey="theme:structure.select-title"
      show={show}
      onClose={onClose}
      size="xl"
      scrollable
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {t('common:button.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave}>
            {t('common:button.save')}
          </button>
        </>
      }
    >
      <div className="d-flex align-items-center gap-2 mb-3 sticky-top bg-blue-100 p-2 rounded">
        <input
          type="text"
          className="form-control"
          placeholder={t('common:search')}
          value={search}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
        />
        <select
          className="form-select"
          style={{ width: 'auto' }}
          value={scopeFilter}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => setScopeFilter(e.target.value)}
        >
          <option value="all">{t('common:all')}</option>
          <option value="global">{t('common:global')}</option>
          <option value="organization">{t('common:organization')}</option>
          <option value="selected">{t('common:selected')}</option>
        </select>
      </div>
      {filtered.length === 0 ? (
        t('theme:structure.no-available')
      ) : (
        <div className="d-flex flex-wrap justify-content-start gap-r-2">
          {filtered.map((page) => (
            <div key={page.superId} className="mb-3">
              <StructurePreview
                onClick={() => handlePreviewClick(page.superId)}
                template={page}
                active={newSelected.includes(page.superId)}
                aspectRatio={aspectRatio}
                inModal
              />
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export const StructureList = () => {
  const { t } = useTranslation('theme');

  const { documentId } = useDocumentParams();
  const { selectedOrganization } = useUser();
  const [showSelectModal, setShowSelectModal] = useState(false);
  const { data: selectedTemplates, isLoading: selectedTemplatesIsLoading } = useSelectedTemplates(documentId);
  const { documentPermissions } = usePermissions(documentId);
  const aspectRatio = useDocumentAspectRatio();
  const { data, isLoading } = useTemplates({
    organization: selectedOrganization,
    type: 'template',
    documentSuperId: documentId,
  });

  if (isLoading || selectedTemplatesIsLoading) {
    return <Spinner />;
  }

  const pages = (data ?? []) as PageStructure[];
  const selected = selectedTemplates ?? [];

  const handleClickAddStructure = () => {
    setShowSelectModal(true);
  };
  const handleCloseSelectModal = () => {
    setShowSelectModal(false);
  };

  return (
    <>
      <div className="d-flex flex-row justify-content-between align-items-center mb-3">
        <h2 className="m-0">{t('structure')}</h2>
        {(documentPermissions.includes('DESIGN') || documentPermissions.includes('CODER')) && (
          <button
            type="button"
            onClick={handleClickAddStructure}
            className="btn-clear rounded-circle aspect-1x1 bg-green-700 text-white d-flex justify-content-center align-items-center "
            style={{ height: '43px', fontSize: '15px' }}
          >
            <i className="fa-regular fa-ellipsis-vertical fa-center-fix"></i>
          </button>
        )}
      </div>
      {pages.length === 0 ? (
        t('structure.no-selected')
      ) : (
        <div className="d-flex flex-wrap justify-content-start gap-r-2">
          {pages
            .filter((page) => selected.includes(page.superId))
            .map?.((page) => (
              <div key={page.superId} className="mb-3">
                <StructurePreview template={page} aspectRatio={aspectRatio} />
              </div>
            ))}
        </div>
      )}
      <StructureModal pageStructures={pages} selected={selected} show={showSelectModal} onClose={handleCloseSelectModal} aspectRatio={aspectRatio} />
    </>
  );
};
